import { PaymentDirection, type PaymentFeeRail } from "../types/paymentFees.types";

/**
 * The provider capability catalog, and the corridor catalog **derived from it**.
 *
 * ## Where the corridor list comes from
 *
 * Nowhere in the backend contract, which is worth being precise about: `CreatePaymentRouteDto` validates
 * `country` as `@Length(3, 3)` and `currency` as `@Length(3, 8)` — free strings, not the `Country` enum — the
 * `Country` / `SUPPORTED_*_RAILS` constants apply only to the payin/payout/quote DTOs, no service validates
 * that `country`, `rail` and `currency` are coherent with each other, and no endpoint enumerates corridors.
 * The backend's actual answer to "which corridors exist" IS the routes table this screen edits.
 *
 * So the catalog is derived from the one backend-grounded constraint that does exist: **what the adapters
 * declare**. A route outside it resolves fine and then strands every payout (see below), which makes the
 * union of `capabilities()` the only defensible row set — and it is per (corridor, direction), because an
 * adapter can land one direction before the other.
 *
 * Deriving it from the dispersions wizard's `RUNTIME_CORRIDOR_ISO3` would be the wrong source twice over: it
 * is a frontend product decision rather than a backend capability, and it pins one rail per country, so a
 * second rail on the same country (which the backend accepts) could not be expressed at all.
 *
 * ## Why the provider catalog lives in the frontend
 *
 * `provider_code` must match a registered adapter's `providerCode` **exactly**, and coloca-nest exposes no
 * endpoint listing the registry (`ProviderRegistry` has `has` / `get` / `supports`, none of them published).
 * Without a catalog the field would have to be free text, where a single typo creates a route that resolves
 * fine but strands every payout: `PayoutService.dispatch` checks `registry.supports(...)` and throws
 * `provider_not_available` BEFORE claiming the row, leaving the payout `CREATED` with its funds reserved —
 * i.e. in the payout-rescue queue.
 *
 * So this mirrors the adapters instead. The codes are code identifiers, which project rule 12 explicitly
 * allows; what reaches the screen is never the code but a label derived from the corridors a provider covers
 * (see `providerCoverageChips`). Same shape as `ACCESS_CATALOG` in settings, which mirrors the backend RBAC
 * catalog for the same reason.
 *
 * **Adding a PSP:** append its entry here with the corridors its `capabilities()` declares. That already
 * requires a backend release (the adapter goes into the `PAYMENT_PROVIDER` factory's `inject` in
 * `PaymentsModule`), so the two land together. A drift only ever _narrows_ what the create dialog offers — it
 * can never produce a wrong `provider_code` — and an unknown code coming back from the API still renders,
 * flagged as unrecognised.
 */

/** One corridor a provider declares, with the directions it declares it for. */
export interface PaymentProviderCorridor {
  rail: PaymentFeeRail;
  /** ISO alpha-3. */
  country: string;
  currency: string;
  directions: readonly PaymentDirection[];
}

export interface PaymentProviderEntry {
  /** Adapter `providerCode`. Never rendered — rule 12. */
  code: string;
  corridors: readonly PaymentProviderCorridor[];
}

const BOTH_DIRECTIONS = [PaymentDirection.PAYIN, PaymentDirection.PAYOUT] as const;
const PAYIN_ONLY = [PaymentDirection.PAYIN] as const;

/**
 * Mirrors `capabilities()` of every adapter aggregated under `PAYMENT_PROVIDER` in coloca-nest
 * `payments.module.ts`. Verified against `bitso-payment.provider.ts`, `kamin-payment.provider.ts` and
 * `bepay-payment.provider.ts`.
 */
export const PAYMENT_PROVIDERS: readonly PaymentProviderEntry[] = [
  {
    code: "BITSO",
    corridors: [
      { rail: "SPEI", country: "MEX", currency: "MXN", directions: BOTH_DIRECTIONS },
      { rail: "PIX", country: "BRA", currency: "BRL", directions: BOTH_DIRECTIONS },
      { rail: "CBU_CVU", country: "ARG", currency: "ARS", directions: BOTH_DIRECTIONS },
    ],
  },
  {
    code: "KAMIN",
    corridors: [{ rail: "BREB", country: "COL", currency: "COP", directions: BOTH_DIRECTIONS }],
  },
  {
    code: "BEPAY",
    corridors: [
      { rail: "NEQUI", country: "COL", currency: "COP", directions: PAYIN_ONLY },
      { rail: "BANCOLOMBIA_BUTTON", country: "COL", currency: "COP", directions: PAYIN_ONLY },
    ],
  },
];

/** A corridor identity, without the provider. */
export interface CorridorIdentity {
  rail: PaymentFeeRail;
  country: string;
  currency: string;
  direction: PaymentDirection;
}

function compareCorridors(a: CorridorIdentity, b: CorridorIdentity): number {
  return (
    a.country.localeCompare(b.country) ||
    a.rail.localeCompare(b.rail) ||
    a.currency.localeCompare(b.currency) ||
    // Payins before payouts: money in, then money out.
    (a.direction === b.direction ? 0 : a.direction === PaymentDirection.PAYIN ? -1 : 1)
  );
}

/**
 * The rows of the page: every (corridor, direction) at least one adapter declares, deduplicated and sorted.
 *
 * Per direction, not `corridor × 2`: an adapter can declare one direction before the other, and a row for a
 * direction nothing can execute would only invite creating a route that strands operations.
 *
 * Sorted rather than left in catalog order, so integrating a PSP appends its corridors instead of reshuffling
 * the existing rows. Deduplicated because two adapters may declare the same corridor — the row is the
 * corridor; which providers can serve it is a separate question (`providersForCorridor`).
 */
export const OPERATIONAL_CORRIDOR_CELLS: readonly CorridorIdentity[] = (() => {
  const byKey = new Map<string, CorridorIdentity>();
  for (const provider of PAYMENT_PROVIDERS) {
    for (const corridor of provider.corridors) {
      for (const direction of corridor.directions) {
        const cell: CorridorIdentity = {
          rail: corridor.rail,
          country: corridor.country,
          currency: corridor.currency,
          direction,
        };
        byKey.set(corridorCellKey(cell), cell);
      }
    }
  }
  return [...byKey.values()].sort(compareCorridors);
})();

/**
 * Distinct corridors, direction dropped — the options of the create dialog's corridor selector. Derived from
 * the cells, so the selector can never offer a corridor the page has no row for.
 */
export const OPERATIONAL_CORRIDORS: readonly Omit<CorridorIdentity, "direction">[] = (() => {
  const byKey = new Map<string, Omit<CorridorIdentity, "direction">>();
  for (const { rail, country, currency } of OPERATIONAL_CORRIDOR_CELLS) {
    byKey.set(corridorTripleKey({ rail, country, currency }), { rail, country, currency });
  }
  return [...byKey.values()];
})();

/**
 * Directions at least one adapter declares for a corridor. The create dialog disables the others: a route
 * there would resolve and then fail at dispatch.
 */
export function declaredDirectionsFor(corridor: Omit<CorridorIdentity, "direction">): PaymentDirection[] {
  return OPERATIONAL_CORRIDOR_CELLS.filter(
    (cell) =>
      cell.country === corridor.country && cell.rail === corridor.rail && cell.currency === corridor.currency,
  ).map((cell) => cell.direction);
}

/** `"MEX|SPEI|MXN"` — a corridor without its direction, as used by the selectors and the form. */
export function corridorTripleKey(corridor: Omit<CorridorIdentity, "direction">): string {
  return `${corridor.country}|${corridor.rail}|${corridor.currency}`;
}

/** `"MEX|SPEI|MXN|PAYOUT"` — the stable identity of a corridor cell, used as key and URL selection. */
export function corridorCellKey(corridor: CorridorIdentity): string {
  return `${corridor.country}|${corridor.rail}|${corridor.currency}|${corridor.direction}`;
}

export function findProvider(code: string): PaymentProviderEntry | undefined {
  return PAYMENT_PROVIDERS.find((provider) => provider.code === code);
}

/**
 * Whether the provider's adapter declares this exact corridor — the same four-way comparison
 * `ProviderRegistry.supports` runs. An unknown code answers `false`: it cannot be resolved to an adapter at
 * all, so a payout on it would fail even earlier, in `getPayoutProvider`.
 */
export function providerDeclaresCorridor(code: string, corridor: CorridorIdentity): boolean {
  const provider = findProvider(code);
  if (!provider) return false;
  return provider.corridors.some(
    (candidate) =>
      candidate.rail === corridor.rail &&
      candidate.country === corridor.country &&
      candidate.currency === corridor.currency &&
      candidate.directions.includes(corridor.direction),
  );
}

/**
 * Providers whose adapter declares this corridor — the options the create dialog offers. Usually exactly one,
 * because no two adapters overlap today; the dialog still renders it as a choice so a second provider on a
 * corridor needs no code change here.
 */
export function providersForCorridor(corridor: CorridorIdentity): PaymentProviderEntry[] {
  return PAYMENT_PROVIDERS.filter((provider) => providerDeclaresCorridor(provider.code, corridor));
}

/**
 * A provider's declared coverage as `["MEX · SPEI", "BRA · PIX", …]` — how a provider is named on screen
 * without naming it. `formatRail` is applied by the caller, which has access to the display helper.
 */
export function providerCoverage(code: string): readonly Omit<CorridorIdentity, "direction">[] {
  return (
    findProvider(code)?.corridors.map(({ rail, country, currency }) => ({ rail, country, currency })) ?? []
  );
}
